import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { BellSvg } from "../CommonSvg";
import {
  getNotificationCountRequest,
  getNotificationListRequest,
  readNotificationRequest,
} from "../../redux/notifications/action";

/* how many rows the panel shows - it is a glance, not the archive */
const PANEL_SIZE = 15;

/* the badge refreshes on its own so a notification that arrives while the tab
   is open still shows up without a reload */
const POLL_MS = 60000;

const LIST_DATA = { pagination: { limit: PANEL_SIZE, page: 1 } };

/* Where a notification takes you. Every one of them is about a document, and
   the two portals keep their documents in different places - which side we are
   on is read from the path, the same way invokeApi picks its token. */
const DOCUMENTS_ADMIN = "/admin/document-management";
const DOCUMENTS_VENDOR = "/vendor/documents";

/* "3 min ago" reads better than a timestamp for something this recent, and the
   full stamp is still there on hover */
const ago = (value) => {
  const date = moment(value);
  return date.isValid() ? date.fromNow() : "";
};

const stamp = (value) => {
  const date = moment(value);
  return date.isValid() ? date.format("MM-DD-YYYY hh:mm A") : "";
};

/* The header bell, shared by the admin and the vendor portal.
 *
 * Both call the same endpoints - they live in vendor_portal and answer
 * according to the token - so nothing here needs to know which side it is on. */
function NotificationBell({
  className = "",
  unreadCount,
  list,
  loading,
  getCount,
  getList,
  markRead,
}) {
  const [open, setOpen] = useState(false);
  /* Where the bell is on screen, handed to the panel as CSS variables.
     - room: the distance from the left edge of the screen to the bell. The
       panel hangs off the bell's right edge, so this - not the window width -
       is what it has to fit into on a desktop.
     - top: where the bell ends. On a phone the panel stops hanging off the
       bell and spans the page's own width instead, which means it can no
       longer take its vertical place from it. */
  const [anchor, setAnchor] = useState(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    getCount();
    const timer = setInterval(getCount, POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* measured while it is open, so a rotated phone or a resized window gets
     the panel resized with it rather than keeping the old fit. Layout rather
     than plain effect, so the panel is never painted at its unclipped width
     first. */
  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    const measure = () => {
      if (wrapRef.current) {
        const rect = wrapRef.current.getBoundingClientRect();
        setAnchor({ room: rect.right, top: rect.bottom });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  /* clicking anywhere else closes it - the panel is an overlay, not a page */
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocumentClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      /* the rows are only fetched when the panel is actually opened; the badge
         is what the closed header needs and it is polled on its own */
      if (!wasOpen) {
        getList(LIST_DATA);
      }
      return !wasOpen;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRowClick = (row) => {
    if (!row.isRead) {
      markRead({ notificationId: row.id, listData: LIST_DATA });
    }

    /* the panel is an overlay - leaving it open over the page it just moved
       to would cover the thing the user came to look at */
    setOpen(false);

    const isAdminArea = location.pathname.split("/")[1] === "admin";
    const target = isAdminArea ? DOCUMENTS_ADMIN : DOCUMENTS_VENDOR;

    /* the id travels so the page can single the document out; landing on the
       list is the point, and it works whether or not the page uses it */
    navigate(target, { state: { documentId: row.referenceId } });
  };

  return (
    <div className={`hra-bell${className ? ` ${className}` : ""}`} ref={wrapRef}>
      <button
        type="button"
        className="hra-bell-btn"
        onClick={toggle}
        aria-label="Notifications"
      >
        <BellSvg />
        {unreadCount > 0 && (
          <span className="hra-bell-dot">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* outside the panel on purpose: the panel clips its overflow, so a
              caret nested inside it would be cut off rather than drawn */}
          <span className="hra-bell-caret" />

          <div
            className="hra-bell-panel"
            style={
              anchor
                ? {
                    "--hra-bell-room": `${anchor.room}px`,
                    "--hra-bell-top": `${anchor.top}px`,
                  }
                : undefined
            }
          >
            <div className="hra-bell-head">
            <b>
              Notifications
              {unreadCount > 0 && (
                <span className="hra-bell-count">{unreadCount}</span>
              )}
            </b>
            {unreadCount > 0 && (
              <button
                type="button"
                className="hra-bell-all"
                onClick={() => markRead({ listData: LIST_DATA })}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="hra-bell-body">
            {loading && list.length === 0 ? (
              <p className="hra-bell-empty">Loading…</p>
            ) : list.length === 0 ? (
              <p className="hra-bell-empty">Nothing here yet.</p>
            ) : (
              list.map((row) => (
                <button
                  type="button"
                  key={row.id}
                  className={`hra-bell-row${row.isRead ? "" : " unread"}`}
                  onClick={() => onRowClick(row)}
                  title={stamp(row.createdDate)}
                >
                  {/* the kind of event, as a colour rather than a word - the
                      message already says what happened */}
                  <span className={`hra-bell-kind k-${row.type}`} />
                  <span className="hra-bell-text">
                    <span className="hra-bell-msg">{row.message}</span>
                    <span className="hra-bell-meta">
                      {row.vendorName ? `${row.vendorName} · ` : ""}
                      {ago(row.createdDate)}
                    </span>
                  </span>
                </button>
              ))
            )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const mapStateToProps = ({ notifications }) => ({
  unreadCount: notifications?.unreadCount || 0,
  list: notifications?.list || [],
  loading: !!notifications?.loading,
});

const mapDispatchToProps = (dispatch) => ({
  getCount: () => dispatch(getNotificationCountRequest()),
  getList: (data) => dispatch(getNotificationListRequest(data)),
  markRead: (data) => dispatch(readNotificationRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationBell);
